import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { Product } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent } from '@nucleus-commerce/ui/components/card';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@nucleus-commerce/ui/components/tooltip';

import { MediaRichTextEditor } from '@/features/media';
import { stripHtml } from '@/features/seo';

import { slugify } from '../api/mappers';
import { useProductScope } from '../hooks/use-product-scope';
import { useSkuRuleResolve } from '../hooks/use-sku-rule-resolve';
import type { ProductFormInput } from '../schemas/product-form-schema';

import { RegenerateSkusDialog } from './regenerate-skus-dialog';
import { SkuOriginChip } from './sku-origin-chip';
import { skuProblemWords } from './sku-words';
import { VendorCombobox } from './vendor-combobox';

export interface BasicsSectionProps {
  /** A product that already has a handle keeps it; only a new product follows the title. */
  handleFollowsTitle: boolean;
}

/** Title, description and, once added, subtitle: what every store shares unless it overrides it. */
export function BasicsSection({ handleFollowsTitle }: BasicsSectionProps) {
  const { readOnly } = useProductScope();
  return (
    <Card className="py-4">
      <CardContent className="flex flex-col gap-3 px-4">
        {readOnly ? <BasicsValues /> : <BasicsFields handleFollowsTitle={handleFollowsTitle} />}
      </CardContent>
    </Card>
  );
}

function BasicsFields({ handleFollowsTitle }: BasicsSectionProps) {
  const { t } = useTranslation('products');
  const form = useFormContext<ProductFormInput>();
  const subtitle = useWatch({ control: form.control, name: 'subtitle' });
  const subtitleError = form.formState.errors.subtitle != null;
  // Stays once shown, so clearing the text while typing doesn't take the field away.
  const [subtitleField, setSubtitleField] = useState<'hidden' | 'shown' | 'added'>('hidden');
  if (subtitleField === 'hidden' && (subtitle !== '' || subtitleError)) setSubtitleField('shown');

  return (
    <>
      <FormField
        control={form.control}
        name="title"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('fields.title')}</FormLabel>
            <FormControl>
              <Input
                autoFocus={handleFollowsTitle}
                placeholder={t('rail.basics.titlePlaceholder')}
                {...field}
                onChange={(e) => {
                  // The handle's input lives in the search card: a handle still equal to what the
                  // old title gave has not been changed by hand, so it keeps following.
                  const handle = form.getValues('handle');
                  const followed = handle === '' || handle === slugify(field.value);
                  field.onChange(e);
                  if (handleFollowsTitle && followed) {
                    form.setValue('handle', slugify(e.target.value), { shouldDirty: true });
                  }
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="descriptionHtml"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('fields.description')}</FormLabel>
            <FormControl>
              <MediaRichTextEditor
                value={field.value}
                onChange={(html) => field.onChange(html ?? '')}
                onBlur={field.onBlur}
                aria-label={t('fields.description')}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {subtitleField === 'hidden' ? (
        <div>
          <Button
            type="button"
            variant="link"
            size="xs"
            className="text-primary h-auto p-0 text-[13px]"
            onClick={() => setSubtitleField('added')}
          >
            {t('rail.basics.addSubtitle')}
          </Button>
        </div>
      ) : (
        <FormField
          control={form.control}
          name="subtitle"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('fields.subtitle')}</FormLabel>
              <FormControl>
                <Input
                  autoFocus={subtitleField === 'added'}
                  placeholder={t('rail.basics.subtitlePlaceholder')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </>
  );
}

/** The same three as plain values, for a store's page. */
function BasicsValues() {
  const { t } = useTranslation('products');
  const { control } = useFormContext<ProductFormInput>();
  const [title, subtitle, description] = useWatch({
    control,
    name: ['title', 'subtitle', 'descriptionHtml'],
  });
  const text = stripHtml(description);
  return (
    <>
      <div className="flex flex-col gap-0.5">
        <span className="text-muted-foreground text-xs">{t('fields.title')}</span>
        <span className="text-sm">{title}</span>
      </div>
      {subtitle ? (
        <div className="flex flex-col gap-0.5">
          <span className="text-muted-foreground text-xs">{t('fields.subtitle')}</span>
          <span className="text-sm">{subtitle}</span>
        </div>
      ) : null}
      <div className="flex flex-col gap-0.5">
        <span className="text-muted-foreground text-xs">{t('fields.description')}</span>
        <span className="text-sm">
          {text || <span className="text-muted-foreground">{t('rail.notSet')}</span>}
        </span>
      </div>
    </>
  );
}

export interface SkuRootFieldProps {
  /** The saved product on an edit page, null while creating. */
  saved: Product | null;
}

/**
 * Style number (SKU root) of the master product; variant SKUs derive from it. While creating,
 * the SKU rule of the chosen product type says whether a blank field gets a generated number.
 * Once saved, the chip says where the value came from and the rule's problem, if any, why
 * there is none.
 */
export function SkuRootField({ saved }: SkuRootFieldProps) {
  const { t } = useTranslation('products');
  const form = useFormContext<ProductFormInput>();
  const productTypeId = form.watch('productTypeId');
  const resolve = useSkuRuleResolve(productTypeId || null, saved === null);
  const [regenerating, setRegenerating] = useState(false);

  // A rule that allocates shows its sample; one without a root template leaves the number to the
  // person. A rule that cannot render for this product (a default rule's {type} with no type)
  // writes nothing, so neither applies.
  const rule = saved === null ? resolve.data : undefined;
  const placeholder = rule?.rule
    ? rule.allocatesRoot && rule.sampleRoot
      ? t('fields.skuRootGeneratedPlaceholder', { sample: rule.sampleRoot })
      : rule.rule.rootTemplate === null
        ? t('fields.skuRootManualPlaceholder')
        : undefined
    : undefined;

  const originOf = (value: string): 'generated' | 'manual' | null => {
    if (!value) return null;
    if (saved === null) return 'manual';
    if (value !== (saved.skuRoot ?? '')) return 'manual';
    return saved.skuRootIsManual ? 'manual' : 'generated';
  };
  const sequenceText =
    saved?.skuSequence != null
      ? saved.skuRule
        ? t('fields.skuRootSequence', { sequence: saved.skuSequence, rule: saved.skuRule.name })
        : t('fields.skuRootSequenceNoRule', { sequence: saved.skuSequence })
      : null;
  const problem = saved?.skuRule?.problem ?? null;

  return (
    <FormField
      control={form.control}
      name="skuRoot"
      render={({ field }) => {
        const origin = originOf(field.value);
        const chip = origin ? <SkuOriginChip origin={origin} /> : null;
        return (
          <FormItem>
            <FormLabel className="flex-wrap">
              <span>{t('fields.skuRoot')}</span>
              {chip && sequenceText && origin === 'generated' ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="focus-visible:ring-ring/50 inline-flex rounded-full outline-none focus-visible:ring-[3px]"
                        aria-label={sequenceText}
                      >
                        {chip}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>{sequenceText}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                chip
              )}
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder={placeholder}
                maxLength={40}
                className="font-mono text-[13px]"
              />
            </FormControl>
            {problem && saved ? (
              <FormDescription className="flex flex-wrap items-center gap-x-2">
                <span>{t('fields.skuRootProblem', { reason: skuProblemWords(t, problem) })}</span>
                <Can permission="products.write">
                  <Button
                    type="button"
                    variant="link"
                    size="xs"
                    className="h-auto p-0"
                    onClick={() => setRegenerating(true)}
                  >
                    {t('sku.regenerate')}
                  </Button>
                </Can>
              </FormDescription>
            ) : null}
            <FormMessage />
            {saved ? (
              <RegenerateSkusDialog
                product={saved}
                open={regenerating}
                onOpenChange={setRegenerating}
              />
            ) : null}
          </FormItem>
        );
      }}
    />
  );
}

/** Vendor of the master product, picked from the vendors already in use or added new. */
export function VendorField() {
  const { t } = useTranslation('products');
  const form = useFormContext<ProductFormInput>();
  return (
    <FormField
      control={form.control}
      name="vendor"
      render={({ field, fieldState }) => (
        <FormItem>
          <FormLabel>{t('fields.vendor')}</FormLabel>
          <FormControl>
            <VendorCombobox
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              aria-invalid={fieldState.invalid}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
